/**
 * VoiceActionExecutor.js
 * Multi-action execution engine for the Kisan Mithr Global Voice Assistant.
 * 
 * Executes structured website action plans sequentially with:
 * - Route readiness detection
 * - Sensitive information protection
 * - React controlled input synchronization
 * - Dynamic scroll container awareness
 * - Short, professional spoken responses
 */

import { resolveRoute, getPageByPath, isRouteAllowed } from "./PageRouteRegistry.js";
import useLanguageStore from "../store/useLanguageStore.js";
import useUserStore from "../store/useUserStore.js";
import {
  findInputField,
  setReactInputValue,
  clearInputField,
  focusInputField,
  findAndClickButton,
  performScroll,
  findAndSelectOption,
  findAndToggle,
  isSensitiveField,
  findAndSwitchTab,
  controlMenu,
  submitActiveForm
} from "./UITargetController.js";

/**
 * Polls for DOM readiness after a navigation or state change.
 */
const waitForElement = async (finderFn, timeoutMs = 1600) => {
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    const el = finderFn();
    if (el) return el;
    await new Promise((r) => setTimeout(r, 60));
  }
  return null;
};

/**
 * Executes a single atomic website action.
 */
export const executeSingleAction = async (action, context = {}) => {
  const { navigateFn, authContext, isCancelled } = context;

  if (isCancelled && isCancelled()) {
    return { success: false, reason: "CANCELLED", spokenFeedback: "Action stopped." };
  }

  const actionType = action.type;

  switch (actionType) {
    // ── 1. ROUTE NAVIGATION ──────────────────────────────────────
    case "navigate": {
      const targetQuery = action.target || action.destination || "";
      const page = resolveRoute(targetQuery);

      if (!page) {
        return {
          success: false,
          reason: "ROUTE_NOT_FOUND",
          spokenFeedback: "I couldn't find that page."
        };
      }

      // Resolve current authenticated user from authContext, useUserStore, and localStorage
      let storeUser = null;
      try {
        storeUser = useUserStore.getState()?.user;
      } catch (e) {}

      let localToken = null;
      try {
        localToken = localStorage.getItem("auth_token");
      } catch (e) {}

      let userStorageObj = null;
      try {
        const raw = localStorage.getItem("user-storage");
        if (raw) {
          const parsed = JSON.parse(raw);
          userStorageObj = parsed?.state?.user;
        }
      } catch (e) {}

      const currentUser = authContext?.user || storeUser || userStorageObj || (localToken ? { role: "farmer" } : null);
      const userRole = currentUser?.role?.toLowerCase() || (currentUser ? "farmer" : "guest");

      if (!isRouteAllowed(page, userRole)) {
        return {
          success: false,
          reason: "PERMISSION_DENIED",
          spokenFeedback: !currentUser
            ? "Please log in to access this page."
            : "You don't have permission to access that page."
        };
      }

      let destination = page.path;
      // Parametric deep linking if requested (e.g., ?crop=rice or ?location=Warangal)
      if (action.parameters?.crop) {
        destination = `${destination}?crop=${encodeURIComponent(action.parameters.crop)}`;
      } else if (action.parameters?.location) {
        destination = `${destination}?location=${encodeURIComponent(action.parameters.location)}`;
      }

      if (typeof navigateFn === "function") {
        navigateFn(destination);
      } else {
        window.location.href = destination;
      }

      return {
        success: true,
        actionType: "navigate",
        destination,
        pageName: page.name,
        spokenFeedback: `Opening ${page.name}.`
      };
    }

    // ── 2. SCROLLING ─────────────────────────────────────────────
    case "scroll": {
      const direction = action.direction || "down";
      const amount = action.amount || "medium";
      const scrollResult = performScroll(direction, amount);
      return {
        success: scrollResult.success,
        actionType: "scroll",
        spokenFeedback: direction === "up" ? "Scrolling up." : "Scrolling down."
      };
    }

    case "scroll_top": {
      performScroll("top", "large");
      return { success: true, actionType: "scroll", spokenFeedback: "Moved to the top." };
    }

    case "scroll_bottom": {
      performScroll("bottom", "large");
      return { success: true, actionType: "scroll", spokenFeedback: "Moved to the bottom." };
    }

    // ── 2b. HISTORY NAVIGATION ─────────────────────────────────────
    case "go_back": {
      window.history.back();
      return { success: true, actionType: "go_back", spokenFeedback: "Going back." };
    }
    case "go_forward": {
      window.history.forward();
      return { success: true, actionType: "go_forward", spokenFeedback: "Going forward." };
    }
    case "reload": {
      window.location.reload();
      return { success: true, actionType: "reload", spokenFeedback: "Refreshing page." };
    }

    // ── 3. FIELD INPUT ENTRY ─────────────────────────────────────
    case "fill_input": {
      const targetName = action.target || "";
      const value = action.value || "";

      // Security Gate: Reject sensitive fields
      if (isSensitiveField(null, targetName) || isSensitiveField(null, value)) {
        return {
          success: false,
          reason: "SENSITIVE_FIELD_PROTECTED",
          spokenFeedback: "For your security, please enter the password yourself."
        };
      }

      // Wait for field to mount if user navigated right before this action
      const field = await waitForElement(() => findInputField(targetName), 1800);

      if (!field) {
        return {
          success: false,
          reason: "FIELD_NOT_FOUND",
          spokenFeedback: "I couldn't find that field."
        };
      }

      // Check field itself for sensitivity
      if (isSensitiveField(field, targetName)) {
        return {
          success: false,
          reason: "SENSITIVE_FIELD_PROTECTED",
          spokenFeedback: "For your security, please enter this credential yourself."
        };
      }

      const ok = setReactInputValue(field, value);
      if (!ok) {
        return {
          success: false,
          reason: "INPUT_UPDATE_FAILED",
          spokenFeedback: "Could not enter the value in that field."
        };
      }

      return {
        success: true,
        actionType: "fill_input",
        target: targetName,
        spokenFeedback: "Done."
      };
    }

    // ── 4. CLEAR FIELD ───────────────────────────────────────────
    case "clear_input": {
      const targetName = action.target || "";
      const res = clearInputField(targetName);
      if (!res.success) {
        return {
          success: false,
          reason: res.reason,
          spokenFeedback: "I couldn't find that field to clear."
        };
      }
      return { success: true, actionType: "clear_input", spokenFeedback: "Cleared." };
    }

    // ── 5. FOCUS FIELD ───────────────────────────────────────────
    case "focus_input": {
      const targetName = action.target || "";
      const res = focusInputField(targetName);
      if (!res.success) {
        return {
          success: false,
          reason: res.reason,
          spokenFeedback: "I couldn't find that field."
        };
      }
      return { success: true, actionType: "focus_input", spokenFeedback: "Done." };
    }

    // ── 6. BUTTON CLICK & FORM SUBMIT ───────────────────────────
    case "click":
    case "submit":
    case "press_enter":
    case "enter": {
      const targetName = (action.target || "").trim();

      // If action is explicit form submit without specific button target, or enter
      if ((actionType === "submit" || actionType === "enter" || actionType === "press_enter") && (!targetName || targetName.toLowerCase() === "submit" || targetName.toLowerCase() === "form")) {
        const submitRes = submitActiveForm();
        if (submitRes.success) {
          return {
            success: true,
            actionType: "submit",
            clickedText: submitRes.clickedText,
            spokenFeedback: "Submitted."
          };
        }
      }

      // If active element is an input and user said "enter", dispatch Enter KeyboardEvent
      if ((actionType === "enter" || actionType === "press_enter") && document.activeElement && ["input", "textarea"].includes(document.activeElement.tagName.toLowerCase())) {
        const activeEl = document.activeElement;
        activeEl.dispatchEvent(new KeyboardEvent("keydown", { key: "Enter", code: "Enter", keyCode: 13, which: 13, bubbles: true, cancelable: true }));
        activeEl.dispatchEvent(new KeyboardEvent("keyup", { key: "Enter", code: "Enter", keyCode: 13, which: 13, bubbles: true, cancelable: true }));
        return {
          success: true,
          actionType: "enter",
          spokenFeedback: "Entered."
        };
      }

      const btnTarget = targetName || (actionType === "submit" ? "submit" : "");
      const res = await waitForElement(() => {
        const r = findAndClickButton(btnTarget);
        return r.success ? r : null;
      }, 1500);

      if (!res || !res.success) {
        // Fallback for submit: attempt submitActiveForm
        if (actionType === "submit" || btnTarget.toLowerCase() === "submit") {
          const fallbackSubmit = submitActiveForm();
          if (fallbackSubmit.success) {
            return {
              success: true,
              actionType: "submit",
              clickedText: fallbackSubmit.clickedText,
              spokenFeedback: "Submitted."
            };
          }
        }
        return {
          success: false,
          reason: "BUTTON_NOT_FOUND",
          spokenFeedback: "I couldn't find that button."
        };
      }

      return {
        success: true,
        actionType: "click",
        clickedText: res.clickedText,
        spokenFeedback: "Done."
      };
    }

    // ── 7. DROPDOWN / SELECT ─────────────────────────────────────
    case "select_option": {
      const targetDropdown = action.target || "";
      const optionValue = action.value || "";
      const res = findAndSelectOption(targetDropdown, optionValue);
      if (!res.success) {
        return {
          success: false,
          reason: res.reason,
          spokenFeedback: "I couldn't select that option."
        };
      }
      return {
        success: true,
        actionType: "select_option",
        selectedText: res.selectedText,
        spokenFeedback: "Selected."
      };
    }

    // ── 8. CHECKBOX / TOGGLE ─────────────────────────────────────
    case "toggle": {
      const targetName = action.target || "";
      const desiredState = action.state;
      const res = findAndToggle(targetName, desiredState);
      if (!res.success) {
        return {
          success: false,
          reason: res.reason,
          spokenFeedback: "I couldn't find that setting."
        };
      }
      return {
        success: true,
        actionType: "toggle",
        spokenFeedback: "Done."
      };
    }

    // ── 9. HISTORY / NAVIGATION CONTROLS ─────────────────────────
    case "go_back": {
      if (typeof navigateFn === "function") navigateFn(-1);
      else window.history.back();
      return { success: true, actionType: "go_back", spokenFeedback: "Going back." };
    }

    case "go_forward": {
      if (typeof navigateFn === "function") navigateFn(1);
      else window.history.forward();
      return { success: true, actionType: "go_forward", spokenFeedback: "Going forward." };
    }

    case "reload": {
      window.location.reload();
      return { success: true, actionType: "reload", spokenFeedback: "Refreshing page." };
    }

    // ── 10. CURRENT PAGE CONTEXT QUERY ───────────────────────────
    case "query_page_info": {
      const currentPath = window.location.pathname;
      const page = getPageByPath(currentPath);
      if (page) {
        return {
          success: true,
          actionType: "query_page_info",
          spokenFeedback: `You're on the ${page.name} page.`
        };
      }
      return {
        success: true,
        actionType: "query_page_info",
        spokenFeedback: "You are on Kisan Mithr."
      };
    }

    // ── 11. LOCAL ASSISTANT HANDOFF ──────────────────────────────
    case "open_local_voice_assistant": {
      if (typeof navigateFn === "function") navigateFn("/assistant");
      else window.location.href = "/assistant";
      return {
        success: true,
        actionType: "open_local_voice_assistant",
        spokenFeedback: "Opening Voice Assistant."
      };
    }

    // ── 12. MENU / SIDEBAR CONTROLS ──────────────────────────────
    case "open_menu":
    case "close_menu":
    case "control_menu": {
      const mode = actionType === "close_menu" || action.state === "close" || action.target === "close" ? "close" : "open";
      const res = controlMenu(mode);
      if (res.success) {
        return {
          success: true,
          actionType: "control_menu",
          isOpen: res.isOpen,
          spokenFeedback: res.spokenFeedback || (mode === "open" ? "Opening menu." : "Closing menu.")
        };
      }
      return {
        success: false,
        reason: res.reason || "MENU_CONTROL_FAILED",
        spokenFeedback: res.spokenFeedback || "Could not control the menu."
      };
    }

    // ── 13. APP LANGUAGE SWITCHING ───────────────────────────────
    case "switch_language": {
      const langCode = action.language || "en";
      try {
        useLanguageStore.getState().setLanguage(langCode);
      } catch (err) {
        console.warn("[VoiceActionExecutor] setLanguage error:", err);
      }
      const langNames = { en: "English", te: "Telugu", hi: "Hindi" };
      const name = action.languageName || langNames[langCode] || langCode;
      const feedback = langCode === "te"
        ? "భాష తెలుగులోకి మార్చబడింది."
        : langCode === "hi"
        ? "भाषा हिंदी में बदल दी गई है।"
        : `Switched language to ${name}.`;
      return {
        success: true,
        actionType: "switch_language",
        language: langCode,
        spokenFeedback: feedback
      };
    }

    // ── 14. TAB SWITCHING ────────────────────────────────────────
    case "switch_tab": {
      const targetTab = (action.target || action.value || "").trim();
      const res = findAndSwitchTab(targetTab);

      if (res.success) {
        return {
          success: true,
          actionType: "switch_tab",
          spokenFeedback: `Switched to ${res.tabName || targetTab || "tab"}.`
        };
      }
      return {
        success: false,
        reason: res.reason || "TAB_NOT_FOUND",
        spokenFeedback: `I couldn't find the ${targetTab || "specified"} tab.`
      };
    }

    default:
      return {
        success: false,
        reason: "UNKNOWN_ACTION_TYPE",
        spokenFeedback: "Unsupported website action."
      };
  }
};

/**
 * High-level Plan Executor:
 * Validates the plan schema and executes actions sequentially.
 */
export const executeActionPlan = async (actionPlan, context = {}) => {
  if (!actionPlan) {
    return {
      success: false,
      spokenFeedback: "I couldn't process that command."
    };
  }

  // Handle local assistant handoff immediately
  if (actionPlan.intent === "LOCAL_ASSISTANT_HANDOFF") {
    return {
      success: true,
      isHandoff: true,
      spokenFeedback: actionPlan.spokenFeedback || "That's a detailed request. Please use the AI Voice Assistant."
    };
  }

  // Handle sensitive refusal immediately
  if (actionPlan.intent === "SENSITIVE_REFUSAL") {
    return {
      success: false,
      spokenFeedback: actionPlan.spokenFeedback || "For your security, please enter that credential yourself."
    };
  }

  // Extract actions array
  const actions = Array.isArray(actionPlan.actions)
    ? actionPlan.actions
    : actionPlan.actionType
    ? [{ type: actionPlan.actionType, ...actionPlan }]
    : [];

  if (actions.length === 0) {
    // If it's a conversational reply
    if (actionPlan.spokenFeedback) {
      return {
        success: true,
        spokenFeedback: actionPlan.spokenFeedback
      };
    }
    return {
      success: false,
      spokenFeedback: "I couldn't determine the website action."
    };
  }

  const executed = [];
  let finalFeedback = actionPlan.spokenFeedback || "";

  for (let i = 0; i < actions.length; i++) {
    const act = actions[i];
    const result = await executeSingleAction(act, context);

    if (!result.success) {
      console.warn(`[VoiceActionExecutor] Action ${i + 1} (${act.type}) failed:`, result.reason);
      return {
        success: false,
        failedAction: act,
        reason: result.reason,
        spokenFeedback: result.spokenFeedback || "I couldn't complete that action.",
        executedActions: executed
      };
    }

    executed.push(result);

    // If no custom spokenFeedback was specified on the overall plan, use the last action's feedback
    if (!actionPlan.spokenFeedback && result.spokenFeedback) {
      finalFeedback = result.spokenFeedback;
    }
  }

  // Generate concise combined response for multi-step navigation + fill / scroll
  if (actions.length > 1 && !actionPlan.spokenFeedback) {
    const hasNav = actions.some((a) => a.type === "navigate");
    if (hasNav) {
      const navAct = actions.find((a) => a.type === "navigate");
      const page = resolveRoute(navAct?.target);
      finalFeedback = `Opening ${page ? page.name : "page"}.`;
    } else {
      finalFeedback = "Done.";
    }
  }

  return {
    success: true,
    spokenFeedback: finalFeedback || "Done.",
    executedActions: executed
  };
};

/**
 * Backward-compatible adapter for existing calls to executeVoiceAction.
 */
export const executeVoiceAction = (intentKey, parameters = {}, authContext = {}, navigateFn) => {
  // Translate legacy intent into an action plan
  let plan = { intent: intentKey, actions: [] };

  if (intentKey === "OPEN_WEATHER") plan.actions = [{ type: "navigate", target: "weather", parameters }];
  else if (intentKey === "OPEN_MANDI") plan.actions = [{ type: "navigate", target: "market", parameters }];
  else if (intentKey === "OPEN_HOME") plan.actions = [{ type: "navigate", target: "dashboard" }];
  else if (intentKey === "OPEN_DASHBOARD") plan.actions = [{ type: "navigate", target: "dashboard" }];
  else if (intentKey === "OPEN_CROP_DISEASE") plan.actions = [{ type: "navigate", target: "crop-health" }];
  else if (intentKey === "OPEN_PEST_DETECT") plan.actions = [{ type: "navigate", target: "pest-detect" }];
  else if (intentKey === "OPEN_MY_FARM") plan.actions = [{ type: "navigate", target: "farm-profile" }];
  else if (intentKey === "OPEN_SCHEMES") plan.actions = [{ type: "navigate", target: "schemes" }];
  else if (intentKey === "OPEN_SATELLITE_COCKPIT") plan.actions = [{ type: "navigate", target: "satellite-cockpit" }];
  else if (intentKey === "OPEN_PESTICIDE_CALC") plan.actions = [{ type: "navigate", target: "pesticide-calc" }];
  else if (intentKey === "OPEN_LIVESTOCK") plan.actions = [{ type: "navigate", target: "livestock" }];
  else if (intentKey === "OPEN_ANALYTICS") plan.actions = [{ type: "navigate", target: "analytics-growth" }];
  else if (intentKey === "OPEN_OVERVIEW") plan.actions = [{ type: "navigate", target: "overview" }];
  else if (intentKey === "OPEN_MARKETPLACE") plan.actions = [{ type: "navigate", target: "buyer-dashboard/market" }];
  else if (intentKey === "OPEN_PROFILE") plan.actions = [{ type: "navigate", target: "profile" }];
  else if (intentKey === "OPEN_NOTIFICATIONS") plan.actions = [{ type: "navigate", target: "notifications" }];
  else if (intentKey === "OPEN_CONTACT") plan.actions = [{ type: "navigate", target: "contact" }];
  else if (intentKey === "OPEN_OUR_TEAM") plan.actions = [{ type: "navigate", target: "our-team" }];
  else if (intentKey === "OPEN_TERMS") plan.actions = [{ type: "navigate", target: "terms" }];
  else if (intentKey === "OPEN_AI_ASSISTANT") plan.actions = [{ type: "open_local_voice_assistant" }];
  else if (intentKey === "SCROLL_DOWN") plan.actions = [{ type: "scroll", direction: "down", amount: "medium" }];
  else if (intentKey === "SCROLL_UP") plan.actions = [{ type: "scroll", direction: "up", amount: "medium" }];
  else if (intentKey === "GO_BACK") plan.actions = [{ type: "go_back" }];
  else if (intentKey === "GO_FORWARD") plan.actions = [{ type: "go_forward" }];
  else if (intentKey === "OPEN_MENU") plan.actions = [{ type: "open_menu" }];
  else if (intentKey === "CLOSE_MENU") plan.actions = [{ type: "close_menu" }];
  else if (intentKey === "SWITCH_LANGUAGE") plan.actions = [{ type: "switch_language", language: parameters.language }];
  else if (intentKey === "CONVERSATIONAL_RESPONSE") {
    return {
      success: true,
      spokenFeedback: parameters.spokenFeedback || "I am here to help you."
    };
  }

  // Asynchronous execution
  executeActionPlan(plan, { navigateFn, authContext }).then((res) => {
    return res;
  });

  return {
    success: true,
    intent: intentKey,
    spokenFeedback: parameters.spokenFeedback || "Opening requested page."
  };
};

const VoiceActionExecutor = {
  executeSingleAction,
  executeActionPlan,
  executeVoiceAction
};

export default VoiceActionExecutor;
